import { forwardRef, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PhotoSection from "./PhotoSection";
import type { Retrospectives } from "../data/types";

interface EditionBlockProps {
  edition: Retrospectives;
  index: number;
  isOpen: boolean;
  toggleOpen: () => void;
}

const EditionBlock = forwardRef<HTMLDivElement, EditionBlockProps>(
  ({ edition, index, isOpen, toggleOpen }, ref) => {
    const pointRef = useRef<HTMLDivElement>(null);
    const isEven = index % 2 === 0;
    const bgColor = isEven ? "bg-white" : "bg-bleuClair";
    const textColor = isEven ? "text-bleuClair" : "text-white";
    const textAlign = isEven ? "ml-auto text-left" : "mr-auto text-right";
    const copyrightsAlign = isEven ? "text-right" : "text-left";
    const yearAlign = isEven ? "left-full sm:ml-2 -ml-2" : "right-full sm:mr-2 -mr-2 text-right";
    const gridAlign = isEven ? "md:grid-cols-[2fr_1fr]" : "md:grid-cols-[1fr_2fr]";
    const textOrder = isEven ? "order-1" : "order-2";
    const photoOrder = isEven ? "order-2" : "order-1";

    const previewPhotos = edition.photos_header.slice(0, 3);

    return (
      <div className="w-full flex justify-between items-start relative my-12">
        {/* Point SVG + année */}
        <div
          ref={pointRef}
          className={`absolute top-1/2 transform -translate-y-1/2 ${isEven ? "-left-4" : "-right-4"}`}
        >
          <div className="w-4 h-4 rounded-full bg-rouge z-10"></div>

          <div
            className={`absolute top-1/2 transform -translate-y-1/2 text-xl font-bold text-rouge
              ${yearAlign}
              sm:rotate-0 -rotate-90 sm:origin-left origin-center`}
          >
            {edition.year}
          </div>
        </div>

        {/* Bloc principal */}
        <div
          ref={ref as any}
          className={`${bgColor} p-8 shadow-lg w-11/12 md:w-10/12 ${textAlign}`}
        >
          {/* Grille texte / photos */}
          <div
            className={`grid grid-cols-1 md:grid-cols-3 gap-6 items-start ${gridAlign}`}
          >
            {/* Texte */}
            <div
              className={`${textOrder} ${textColor} order-1 md:${textOrder}`}
            >
              <h4 className="text-lg font-semibold font-montserrat text-orange">
                {edition.number === 1 ? (
                  <>
                    1<sup>ère</sup> ÉDITION
                  </>
                ) : (
                  <>
                    {edition.number}
                    <sup>e</sup> ÉDITION
                  </>
                )}
                <span className="hidden sm:inline-block">&nbsp;•&nbsp;</span>
                <br className="block sm:hidden" />
                {edition.date}
              </h4>
              <h3 className="text-5xl font-bebas text-orange mt-1 mb-3">
                {edition.title}
              </h3>

              <hr className="mx-auto my-5 border-t-2 border-orange rounded-full" />

              {edition.description && edition.description.length > 0 && (
                <div className="space-y-3 mb-6">
                  {edition.description.map((paragraph, i) => (
                    <p
                      key={i}
                      className={`font-montserrat text-justify leading-relaxed ${textColor}`}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}

              <h5 className={`font-montserrat font-semibold mb-1 ${textColor}`}>
                IL Y AVAIT :
              </h5>
              <p className={`font-bebas text-3xl ${textColor}`}>
                {(() => {
                  const list = [edition.fanfare, ...edition.groups];
                  const nodes = list.map((g) =>
                    g.website ? (
                      <a
                        key={g.name}
                        href={g.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-orange transition-colors duration-500"
                      >
                        {g.name}
                      </a>
                    ) : (
                      <span key={g.name}>{g.name}</span>
                    )
                  );
                  return nodes.flatMap((node, i) =>
                    i === 0
                      ? [node]
                      : [<span key={`sep-${i}`} className="mx-2 text-orange">—</span>, node]
                  );
                })()}
              </p>
            </div>

            {/* Photos */}
            <div className={`${photoOrder} order-2 md:${photoOrder} flex flex-col items-end`}>
              <div className="grid grid-cols-2 auto-rows-auto gap-2 w-full">
                {previewPhotos[0] && (
                  <img
                    src={previewPhotos[0]}
                    alt="preview_1"
                    className="col-span-2 w-full object-cover rounded-sm"
                  />
                )}
                {previewPhotos.slice(1).map((p, idx) => (
                  <img
                    key={idx}
                    src={p}
                    alt={`preview_${idx + 2}`}
                    className="w-full aspect-square object-cover rounded-sm"
                  />
                ))}
              </div>

              {/* Copyrights */}
              {edition.copyrights && edition.copyrights.length > 0 && (
                <div className={`mt-1 text-[11px] ${textColor} italic ${copyrightsAlign} w-full`}>
                  {edition.copyrights.map((cr, idx) => (
                    <div key={idx}>© {cr}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Toggle */}
          {edition.photos.length > 0 && (
            <button
              onClick={toggleOpen}
              className={`flex items-center ${textColor} mt-2 mb-2 relative overflow-hidden`}
            >
              {isOpen ? <ChevronUp className="mr-1" /> : <ChevronDown className="mr-1" />}
              <AnimatePresence mode="wait">
                <motion.span
                  key={isOpen ? "moins" : "plus"}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="inline-block"
                >
                  {isOpen ? "Moins de photos" : "Plus de photos"}
                </motion.span>
              </AnimatePresence>
            </button>
          )}

          {/* Section photos */}
          <PhotoSection isOpen={isOpen} photos={edition.photos} />
        </div>
      </div>
    );
  }
);

export default EditionBlock;